import type { GardenBed } from '@/data/gardenPlanner';
import type { CanonicalPlant } from '@/lib/normalizePlant';

export type PlannerPlacementCircle = {
  id: string;
  plantId: string;
  x: number;
  y: number;
  radius: number;
};

const GRID_SNAP_INCHES = 6;

function parseSpacingInches(spacing: string): number {
  const match = spacing.match(/(\d+)/);
  return match ? Number.parseInt(match[1], 10) : 12;
}

function normalizeName(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function plantHasAnyTerm(plant: CanonicalPlant, terms: string[]): boolean {
  const haystack = normalizeName([
    plant.id,
    plant.name,
    plant.scientificName,
    plant.type,
    ...(plant.traits ?? []),
  ].join(' '));
  return terms.some(term => haystack.includes(term));
}

function snapToGrid(value: number): number {
  return Math.round(value / GRID_SNAP_INCHES) * GRID_SNAP_INCHES;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function bedSizeInches(bed: GardenBed): { width: number; height: number } {
  return { width: bed.width * 12, height: bed.height * 12 };
}

export function getPlantRadiusInches(
  plantId: string,
  plantLookup: ReadonlyMap<string, CanonicalPlant>,
): number {
  const plant = plantLookup.get(plantId);
  if (!plant) return 6;

  const spacing = parseSpacingInches(plant.spacing);
  let scale = 0.5;

  if (plantHasAnyTerm(plant, ['tomato', 'pepper', 'okra', 'corn'])) scale = 0.42;
  if (plantHasAnyTerm(plant, ['basil', 'parsley', 'cilantro', 'thyme', 'oregano', 'chive'])) scale = 0.38;
  if (plantHasAnyTerm(plant, ['carrot', 'radish', 'beet', 'onion', 'garlic', 'lettuce'])) scale = 0.34;
  if (plantHasAnyTerm(plant, ['squash', 'pumpkin', 'melon', 'cucumber', 'zucchini'])) scale = 0.58;

  return clamp(spacing * scale, 4, 22);
}

export function clampPlacementToBed(
  bed: GardenBed,
  x: number,
  y: number,
  radius: number,
): { x: number; y: number } {
  const size = bedSizeInches(bed);
  return {
    x: clamp(x, radius, Math.max(radius, size.width - radius)),
    y: clamp(y, radius, Math.max(radius, size.height - radius)),
  };
}

export function circlesOverlap(
  left: { x: number; y: number; radius: number },
  right: { x: number; y: number; radius: number },
): boolean {
  const minDistance = left.radius + right.radius;
  return Math.hypot(left.x - right.x, left.y - right.y) < minDistance;
}

export function getCollisionCircles(
  bed: GardenBed,
  plantLookup: ReadonlyMap<string, CanonicalPlant>,
  excludePlacementId?: string,
): PlannerPlacementCircle[] {
  return bed.plants
    .filter(placement => placement.id !== excludePlacementId)
    .filter(placement => Number.isFinite(placement.x) && Number.isFinite(placement.y))
    .filter(placement => plantLookup.has(placement.plantId))
    .map(placement => ({
      id: placement.id,
      plantId: placement.plantId,
      x: placement.x,
      y: placement.y,
      radius: getPlantRadiusInches(placement.plantId, plantLookup),
    }));
}

export function collidesWithPlacedPlants(
  bed: GardenBed,
  plantLookup: ReadonlyMap<string, CanonicalPlant>,
  circle: PlannerPlacementCircle,
  excludePlacementId?: string,
): boolean {
  return getCollisionCircles(bed, plantLookup, excludePlacementId).some(existing => (
    circlesOverlap(circle, existing)
  ));
}

export function resolvePlacement(
  bed: GardenBed,
  plantLookup: ReadonlyMap<string, CanonicalPlant>,
  plantId: string,
  desiredX: number,
  desiredY: number,
  excludePlacementId?: string,
): { x: number; y: number } | null {
  const radius = getPlantRadiusInches(plantId, plantLookup);
  const snapped = clampPlacementToBed(bed, snapToGrid(desiredX), snapToGrid(desiredY), radius);
  const firstCircle = { id: excludePlacementId ?? 'candidate', plantId, x: snapped.x, y: snapped.y, radius };

  if (!collidesWithPlacedPlants(bed, plantLookup, firstCircle, excludePlacementId)) {
    return { x: snapped.x, y: snapped.y };
  }

  const size = bedSizeInches(bed);
  const maxSteps = Math.ceil(Math.max(size.width, size.height) / GRID_SNAP_INCHES);
  const seen = new Set<string>([`${snapped.x}:${snapped.y}`]);

  for (let ring = 1; ring <= maxSteps; ring += 1) {
    const candidates: Array<{ x: number; y: number; distance: number }> = [];

    for (let dx = -ring; dx <= ring; dx += 1) {
      for (let dy = -ring; dy <= ring; dy += 1) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== ring) continue;

        const rawX = snapped.x + dx * GRID_SNAP_INCHES;
        const rawY = snapped.y + dy * GRID_SNAP_INCHES;
        const candidate = clampPlacementToBed(bed, rawX, rawY, radius);
        const key = `${candidate.x}:${candidate.y}`;
        if (seen.has(key)) continue;
        seen.add(key);
        candidates.push({
          x: candidate.x,
          y: candidate.y,
          distance: Math.hypot(candidate.x - desiredX, candidate.y - desiredY),
        });
      }
    }

    candidates.sort((left, right) => left.distance - right.distance);
    for (const candidate of candidates) {
      const circle = { id: excludePlacementId ?? 'candidate', plantId, x: candidate.x, y: candidate.y, radius };
      if (!collidesWithPlacedPlants(bed, plantLookup, circle, excludePlacementId)) {
        return { x: candidate.x, y: candidate.y };
      }
    }
  }

  return null;
}
